// @flow
import { durationLabel, durationValue } from '../util/common';
import { parseDateTime, parseDate } from '../util/date';
import SignProcessCompany from './SignProcessCompany';
import SignProcessConfig from './SignProcessConfig';
import SignProcessDocument from './SignProcessDocument';
import { doGet, doPost, doPostMultipart } from '../util/api';
import ApiResponse from './ApiResponse';
import type { AgreementProps } from './props';
import TransferData from './TransferData';
import PaymentData from './PaymentData';
import SignTarget from './SignTarget';

class SignProcess {
  code: string;
  dateTime: ?Date;
  processType: string;
  state: string;
  price: number;
  daysDuration: number;
  priceCode: string;
  name: string;
  lastname: string;
  email: string;
  phone: string;
  legalIDType: ?string;
  legalID: ?string;
  legalIDExpiresAt: ?string;
  idDocumentCode: ?string;
  tributaryName: ?string;
  tributaryLegalID: ?string;
  countryOfResidence: ?string;
  provinceOfResidence: ?string;
  cityOfResidence: ?string;
  addressOfResidence: ?string;
  zipCode: ?string;
  forInvoicing: boolean;
  paymentType: ?string;
  hasPayValidated: boolean;
  proofOfPaymentName: String;
  verificationVideoName: String;
  paymentTypes: string[];
  validLegalIDTypes: string[];
  genders: string[];
  haveCompanyData: boolean;
  completedCompanyData: boolean;
  companyData: ?SignProcessCompany;
  files: SignProcessDocument[];
  validFileTypes: string[];
  contractAgreementUrl: string;
  templateAgreementUrl: string;
  hasContractAgreement: boolean;
  hasContractAgreementApproved: boolean;
  internalValidation: boolean;
  billToFinalCustomer: boolean;
  billingName: string;
  billingEmail: string;
  billingLegalIDType: string;
  billingLegalID: string;
  billingAddress: string;
  transferData: ?TransferData;
  gender: ?string;
  birthday: ?Date;
  countryOfBirth: ?string;
  targets: SignTarget[];
  containerTitle: string;
  containerDescription: string;
  containerType: string;
  containerPrice: number;
  coupon: string;
  couponDescription: string;

  static parseList(raw?: any): SignProcess[] {
    let list: SignProcess[] = [];
    if (raw && Array.isArray(raw)) {
      for (let item of raw) {
        const process: ?SignProcess = SignProcess.parse(item);
        if (process) {
          list.push(process);
        }
      }
    }
    return list;
  }

  static parse(raw: any): ?SignProcess {
    if (raw) {
      const process = new SignProcess();
      process.copyFrom(raw);
      process.genders = [
        'MALE', 'FEMALE',
      ];
      return process;
    }
  }

  copyFrom(raw: any) {
    this.code = raw.code;
    this.dateTime = parseDateTime(raw.dateTime);
    this.processType = raw.processType;
    this.state = raw.state;
    this.price = raw.price;
    this.daysDuration = raw.daysDuration;
    this.priceCode = raw.priceCode;
    this.name = raw.name;
    this.lastname = raw.lastname;
    this.email = raw.email;
    this.phone = raw.phone;
    this.legalIDType = raw.legalIDType;
    this.legalID = raw.legalID;
    this.legalIDExpiresAt = raw.legalIDExpiresAt;
    this.idDocumentCode = raw.idDocumentCode;
    this.tributaryName = raw.tributaryName;
    this.tributaryLegalID = raw.tributaryLegalID;
    this.countryOfResidence = raw.countryOfResidence;
    this.provinceOfResidence = raw.provinceOfResidence;
    this.cityOfResidence = raw.cityOfResidence;
    this.addressOfResidence = raw.addressOfResidence;
    this.zipCode = raw.zipCode;
    this.forInvoicing = !!raw.forInvoicing;
    this.paymentType = raw.paymentType;
    this.hasPayValidated = !!raw.hasPaymentValidated;
    this.proofOfPaymentName = !!raw.proofOfPaymentName;
    this.verificationVideoName = !!raw.verificationVideoName;
    this.validLegalIDTypes = raw.validLegalIDTypes;
    this.paymentTypes = raw.paymentTypes;
    this.companyData = SignProcessCompany.parse(raw.signProcessCompany);
    this.haveCompanyData = !!raw.haveCompanyData;
    this.completedCompanyData = !!raw.completedCompanyData;
    this.files = SignProcessDocument.parseList(raw.files);
    this.validFileTypes = raw.validFileTypes;
    this.contractAgreementUrl = raw.contractAgreementUrl;
    this.templateAgreementUrl = raw.templateAgreementUrl;
    this.hasContractAgreement = !!raw.contractAgreementUrl;
    this.hasContractAgreementApproved = !!raw.hasContractAgreementApproved;
    this.internalValidation = !!raw.internalValidation;
    this.billToFinalCustomer = !!raw.billToFinalCustomer;
    this.billingName = raw.billingName;
    this.billingEmail = raw.billingEmail;
    this.billingLegalIDType = raw.billingLegalIDType;
    this.billingLegalID = raw.billingLegalID;
    this.billingAddress = raw.billingAddress;
    this.transferData = TransferData.parse(raw.transferData);
    this.gender = raw.gender;
    this.birthday = parseDate(raw.birthday);
    this.countryOfBirth = raw.countryOfBirth;
    this.targets = SignTarget.parseList(raw.targets);
    this.containerTitle = raw.containerTitle;
    this.containerDescription = raw.containerDescription;
    this.containerType = raw.containerType;
    this.containerPrice = raw.containerPrice;
    this.coupon = raw.coupon;
    this.couponDescription = raw.couponDescription;
  }

  isDraft(): boolean {
    return this.state === 'DRAFT';
  }

  isDataLoaded(): boolean {
    return this.state === 'DATA_LOADED';
  }

  hasDataLoaded(): boolean {
    return !this.isDraft();
  }

  isDocumentsLoaded(): boolean {
    return this.state === 'DOCUMENTS_LOADED';
  }

  hasDocuments(): boolean {
    let pendentCount = 0;
    for (let fileType of this.validFileTypes) {
      const file = this.files.find((doc) => doc.fileType === fileType);
      if (!file) {
        pendentCount++;
      }
    }
    return pendentCount === 0;
  }

  hasBilling(): boolean {
    return this.billToFinalCustomer || !!this.billingLegalIDType;
  }

  hasDocumentsLoaded(): boolean {
    return this.hasDataLoaded() && !this.isDataLoaded();
  }

  isBillingLoaded(): boolean {
    return this.state === 'BILLING_LOADED';
  }

  hasBillingLoaded(): boolean {
    return this.hasDocumentsLoaded() && !this.isDocumentsLoaded();
  }

  isPaymentLoaded(): boolean {
    return this.state === 'PAYMENT_LOADED';
  }

  hasPaymentLoaded(): boolean {
    return this.hasBillingLoaded() && !this.isBillingLoaded();
  }

  isAgreementLoaded(): boolean {
    return this.state === 'AGREEMENT_LOADED';
  }

  hasAgreementLoaded(): boolean {
    return this.hasPaymentLoaded() && !this.isPaymentLoaded();
  }

  isVideoLoaded(): boolean {
    return this.state === 'VIDEO_LOADED';
  }

  hasVideoLoaded(): boolean {
    return this.hasAgreementLoaded() && !this.isAgreementLoaded();
  }

  isDocumentsValidated(): boolean {
    return this.state === 'DOCUMENTS_VALIDATED';
  }

  hasDocumentsValidated(): boolean {
    return this.hasVideoLoaded() && !this.isVideoLoaded();
  }

  isBillingValidated(): boolean {
    return this.state === 'BILLING_VALIDATED';
  }

  hasBillingValidated(): boolean {
    return this.hasDocumentsValidated() && !this.isDocumentsValidated();
  }

  isPaymentValidated(): boolean {
    return this.state === 'PAYMENT_VALIDATED';
  }

  hasPaymentValidated(): boolean {
    return this.hasBillingValidated() && !this.isBillingValidated();
  }

  isDataValidated(): boolean {
    return this.state === 'DATA_VALIDATED';
  }

  hasDataValidated(): boolean {
    return this.hasPaymentValidated() && !this.isPaymentValidated();
  }

  isVendorReady(): boolean {
    return this.state === 'VENDOR_READY';
  }

  isVendorProcessing(): boolean {
    return this.state === 'VENDOR_PROCESSING';
  }

  isCustomerActivated(): boolean {
    return this.state === 'CUSTOMER_ACTIVATED';
  }

  isProcessing(): boolean {
    return this.isVendorReady() || this.isVendorProcessing() || this.isCustomerActivated();
  }

  isVendorCompleted(): boolean {
    return this.state === 'VENDOR_COMPLETED';
  }

  isVendorRejected(): boolean {
    return this.state === 'VENDOR_REJECTED';
  }

  isRevoked(): boolean {
    return this.state === 'REVOKED';
  }

  isCancelled(): boolean {
    return this.state === 'CANCELLED';
  }

  isCompleted(): boolean {
    return this.isVendorCompleted()
      || this.isVendorRejected()
      || this.isRevoked()
      || this.isCancelled();
  }

  durationValue(): string {
    return durationValue(this.daysDuration);
  }

  durationLabel(): string {
    return durationLabel(this.daysDuration);
  }

  pendentDocumentTypes(): string[] {
    let filesTypes: string[] = [];
    for (let fileType of this.validFileTypes) {
      const file = this.files.find((doc) => doc.fileType === fileType);
      if (!file) {
        filesTypes.push(fileType);
      }
    }
    return filesTypes;
  }

  isNaturalProcessType(): boolean {
    return this.processType === SignProcessConfig.signProcessTypes.NATURAL_PERSON ||
      SignProcessConfig.signProcessTypes.NATURAL_PERSON_RUC;
  }

  canCreateInvoice(): boolean {
    return this.daysDuration >= 365 && this.isNaturalProcessType();
  }

  isLegalProcessType(): boolean {
    return this.processType === SignProcessConfig.signProcessTypes.LEGAL_PERSON;
  }

  needTributaryId(): boolean {
    return this.forInvoicing && this.isNaturalProcessType();
  }

  fullName(): string {
    let value = this.name;
    if (!!this.lastname) {
      value += ` ${this.lastname}`;
    }
    return value;
  }

  hasSameBillingAndData(): boolean {
    if (this.billToFinalCustomer) {
      return false;
    }
    if (this.billingName === undefined
      && this.billingEmail === undefined
      && this.billingLegalIDType === undefined
      && this.billingLegalID === undefined
      && this.billingAddress === undefined) {
      return true;
    }
    return this.fullName() === this.billingName
      && this.email === this.billingEmail
      && this.legalIDType === this.billingLegalIDType
      && this.legalID === this.billingLegalID
      && this.addressOfResidence === this.billingAddress;
  }

  toDataMap(): any {
    return {
      name: this.name,
      lastname: this.lastname,
      email: this.email,
      phone: this.phone,
      legalIDType: this.legalIDType,
      legalID: this.legalID,
      legalIDExpiresAt: this.legalIDExpiresAt,
      idDocumentCode: this.idDocumentCode,
      tributaryName: this.tributaryName,
      tributaryLegalID: this.tributaryLegalID,
      countryOfResidence: this.countryOfResidence,
      provinceOfResidence: this.provinceOfResidence,
      cityOfResidence: this.cityOfResidence,
      addressOfResidence: this.addressOfResidence,
      zipCode: this.zipCode,
      forInvoicing: this.forInvoicing,
      paymentType: this.paymentType,
      gender: this.gender,
      birthday: this.birthday,
      countryOfBirth: this.countryOfBirth,
      targets: this.targets,
    };
  }

  getContractAgreementUrl(token: ?string): string {
    if (!!token && token.length < 15) {
      return `/customer/retail-sign-process/${this.code}/${token}/contract-agreement`;
    }
    return this.contractAgreementUrl;
  }

  getTemplateAgreementUrl(token: ?string): string {
    if (!!token && token.length < 15) {
      return `/customer/retail-sign-process/${this.code}/${token}/template-agreement`;
    }
    return this.templateAgreementUrl;
  }

  getPaymentData(): Promise<PaymentData> {
    const url = `/customer/payment/sign-process/${this.code}/data`;
    return new Promise(resolve => {
      doGet(url)
        .then((response: ApiResponse) => {
          const data = new PaymentData();
          if (response.ok) {
            data.copyFrom(response.data);
          } else if (!response.is401Error()) {
            data.error = String(response.message);
          }
          resolve(data);
        });
    });
  }

  saveVideo(file: File, notAttached?: Boolean): Promise<ApiResponse> {
    const data = {
      name: file.name,
      notAttached: !!notAttached,
    };
    const files = {
      file: file,
    };
    const url = `/customer/sign-process/${this.code}/verification-video`;
    return new Promise(resolve => {
      doPostMultipart(url, data, files)
        .then((response: ApiResponse) => {
          resolve(response);
        });
    });
  }

  applyCoupon(coupon?: string): Promise<ApiResponse> {
    const data = {
      coupon,
    };
    const url = `/customer/sign-process/${this.code}/apply-coupon`;
    return new Promise(resolve => {
      doPost(url, data)
        .then((response: ApiResponse) => {
          if (response.ok) {
            this.price = response.data.price;
          }
          resolve(response);
        });
    });
  }

  saveAgreement: AgreementProps => Promise<any>;
}

export class ProcessResponse {
  process: SignProcess;
  ok: boolean;
  message: string;
}

function saveAgreement(props: AgreementProps): Promise<ProcessResponse> {
  const { content, file, token } = props;
  const data = {
    signature: content,
    name: file ? file.name : '',
  };
  const files = {
    file,
  };
  let url = `/customer/sign-process/${this.code}/contract-agreement`;
  if (!!token && token.length < 15) {
    url = this.getContractAgreementUrl(token);
  }
  return new Promise(resolve => {
    doPostMultipart(url, data, files)
      .then((response: ApiResponse) => {
        const processResponse = new ProcessResponse();
        processResponse.ok = !!response.ok;
        if (response.ok) {
          this.copyFrom(response.data);
        } else if (!response.is401Error()) {
          processResponse.message = String(response.message);
        }
        processResponse.process = this;
        resolve(processResponse);
      });
  });
}

SignProcess.prototype.saveAgreement = saveAgreement;

export default SignProcess;
